import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Check, Loader2, Warehouse } from 'lucide-react';
import { api, type Garage } from '@/lib/api';
import { trackGarageShare, trackGaragePostShare, trackSaveToGarageCta, trackGaragePostAdded, trackGarageSaveSuccess, trackGarageSaveAbandoned } from '@/lib/analytics';
import { useAuthStore } from '@/stores/auth-store';

interface GarageShareModalProps {
  postId: string;
  // ANALYTICS-021: pass-through so save-to-garage funnel events can be sliced
  // by entry surface (feed card vs article detail page).
  source?: 'feed' | 'detail';
  // ANALYTICS-079: number of garages the user belongs to, for funnel segmentation.
  garageCount?: number;
  // ANALYTICS-083: article category for garage share attribution
  articleCategory?: string;
  // ANALYTICS-085: content source website engagement dimension
  sourceDomain?: string;
  // ANALYTICS-092: publisher display name for source attribution
  sourceName?: string;
  onClose: () => void;
  onSaved?: (garageName: string) => void;
}

export function GarageShareModal({ postId, source = 'feed', garageCount = 0, articleCategory, sourceDomain = 'unknown', sourceName = 'unknown', onClose, onSaved }: GarageShareModalProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [sharedTo, setSharedTo] = useState<string | null>(null);
  // ANALYTICS-079: map source to funnel source type
  const funnelSource = source === 'feed' ? 'feed_card' as const : 'article_footer' as const;

  useEffect(() => {
    trackGarageShare('garage_share_open', postId);
    trackGaragePostShare(postId, '', 'modal_opened', undefined, articleCategory);
    if (!isAuthenticated) {
      // ANALYTICS-021: anon user got past the click and is now staring at the
      // sign-up gate — emit auth_wall_hit so the click→wall conversion is
      // observable on the registration-intent funnel.
      trackSaveToGarageCta('auth_wall_hit', postId, false, source);
    }
  }, [postId, isAuthenticated, source]);

  const { data, isLoading } = useQuery({
    queryKey: ['my-garages'],
    queryFn: () => api.garages.my(),
    enabled: isAuthenticated,
  });

  const shareMutation = useMutation({
    mutationFn: (slug: string) => api.garages.sharePost(slug, postId),
    onSuccess: (_data, slug) => {
      trackGarageShare('garage_share_confirm', postId, slug);
      const matched = garages.find((g: Garage) => g.slug === slug);
      trackGaragePostShare(postId, slug, 'completed', matched?.name, articleCategory);
      trackGaragePostAdded(postId, slug);
      trackGarageSaveSuccess(postId, funnelSource, resolvedGarageCount, sourceDomain, sourceName);
      setSharedTo(slug);
      onSaved?.(matched?.name ?? slug);
      setTimeout(onClose, 1200);
    },
  });

  const garages = data?.items ?? [];
  // ANALYTICS-079: use fetched count when available, fallback to prop
  const resolvedGarageCount = garages.length || garageCount;

  // ANALYTICS-079: fire abandoned event when modal closes without a save
  const handleClose = () => {
    if (!sharedTo) {
      trackGarageSaveAbandoned(postId, funnelSource, resolvedGarageCount, sourceDomain, sourceName);
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-sm rounded-xl border border-border-primary bg-bg-secondary p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-text-primary">Share to Garage</h3>
          <button onClick={handleClose} className="rounded-lg p-1 text-text-tertiary hover:bg-bg-tertiary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!isAuthenticated ? (
          <div className="py-4 text-center">
            <Warehouse className="mx-auto h-8 w-8 text-primary-400" />
            <p className="mt-3 text-sm font-medium text-text-primary">
              Sign up to save articles to a Garage
            </p>
            <p className="mt-1 text-xs text-text-tertiary">
              Build your collection, follow brands, join discussions.
            </p>
            <Link
              to="/signup"
              onClick={handleClose}
              className="mt-4 inline-flex items-center justify-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Sign up — it's free
            </Link>
            <Link
              to="/login"
              onClick={handleClose}
              className="mt-2 block text-xs text-text-tertiary hover:text-text-primary"
            >
              Already have an account? Log in
            </Link>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-text-tertiary" />
          </div>
        ) : garages.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-tertiary">
            You haven't joined any Garages yet.
          </p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {garages.map((garage: Garage) => (
              <button
                key={garage.id}
                disabled={shareMutation.isPending || sharedTo === garage.slug}
                onClick={() => shareMutation.mutate(garage.slug)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-bg-tertiary disabled:opacity-60"
              >
                <span className="text-lg">{garage.emoji || '🚗'}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-text-primary truncate">{garage.name}</div>
                  <div className="text-xs text-text-tertiary">{garage.memberCount} members</div>
                </div>
                {sharedTo === garage.slug && (
                  <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                )}
                {shareMutation.isPending && shareMutation.variables === garage.slug && (
                  <Loader2 className="h-4 w-4 animate-spin text-text-tertiary flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}

        {shareMutation.isError && (
          <p className="mt-2 text-xs text-red-400">Failed to share. Try again.</p>
        )}
      </div>
    </div>
  );
}
